import React from 'react';
import moment from 'moment';
import styles from './styles.scss';
import {AppBar, AppBarContext, AppBarSection, AppBarTitle, Button, ButtonGroup,} from '@densityco/ui';

import {
  CaptureScheduleModalActionTypes,
  CaptureScheduleModalState,
  DAYS_OF_WEEK,
  PAGES_IN_ORDER,
  Status,
} from '../../types/capture-schedule-modal';
import { CaptureScheduleModalSensor } from '.';

import {BackButton, CaptureWarning} from './utils';

import DataGrid from '../DataGrid';

import {
  calculateEndDate,
  calculateTotalCaptures,
  CAPTURE_WARNING_THRESHOLD,
  convertModalDataToCaptureSchedules,
  parseCaptureDurationInSeconds,
  parseCapturesPerSchedule,
  TWENTY_FOUR_HOURS_IN_SECONDS,
} from '../../utils/calculate-capture-schedules';

import colorVariables from '@densityco/ui/variables/colors.json';

import {DispatchType} from '../../types/rx-actions';

const {Fragment} = React;

type SummaryPageProps = {
  state: CaptureScheduleModalState,
  dispatch: DispatchType,
  sensors: Array<CaptureScheduleModalSensor>,
  onDismiss: () => void,
};

function pluralize(num: number, singularSuffix: string): string {
  if (num === 1) {
    return singularSuffix;
  } else {
    return `${singularSuffix}s`;
  }
}

const HOURS_IN_UTC_DAY = [];
const startOfUtcDay = moment.utc().startOf('day');
for (
    let time = startOfUtcDay.clone();
    time.unix() < startOfUtcDay.unix() + TWENTY_FOUR_HOURS_IN_SECONDS;
    time.add(1, 'hour')
) {
  HOURS_IN_UTC_DAY.push([time.format('HH:mm:ss'), time.format('h a')]);
}

const SummaryPage: React.FunctionComponent<SummaryPageProps> = (props) => {
  const {state, dispatch, sensors, onDismiss} = props;

  const captureDuration = parseCaptureDurationInSeconds(state);
  const capturesPerSchedule = parseCapturesPerSchedule(state);

  const svgHeight = 600;
  const svgWidth = 800;

  const padding = {
    top: 24,
    bottom: 24,
    left: 24,
    right: 24,
  };
  const width = svgWidth - padding.left - padding.right;
  const height = svgHeight - padding.top - padding.bottom;

  const timeAxisWidth = 64;
  const chartWidth = width - timeAxisWidth; // Width of the visualization within the svg

  const weekColumnWidth = chartWidth / 7;
  const weekColumnHeaderOffset = weekColumnWidth * 0.5;
  const weekColumnHeaderSchedulesOffset = 32;

  const columnHeight = height - weekColumnHeaderSchedulesOffset;
  const pixelsPerSecond = columnHeight / TWENTY_FOUR_HOURS_IN_SECONDS;

  const captureHeightInPx = captureDuration * pixelsPerSecond;
  const scheduleHeightInPx = capturesPerSchedule * captureHeightInPx;

  const scheduleEndDate = calculateEndDate(state);
  const totalCaptures = calculateTotalCaptures(state);
  const now = moment.utc().startOf('day');

  const numberOfSchedules = state.schedules.reduce((acc, s) => acc + s.daysOfWeek.length, 0),
      durationUntilScheduleEnd = moment.duration(scheduleEndDate.diff(now));

  const captureScheduleRequests = convertModalDataToCaptureSchedules(state, sensors),
      totalCaptureScheduleCount = captureScheduleRequests.flatMap(r => r.body.link_ids).length;

  return (
      <Fragment>
        <div className={styles.summaryWrapper}>
          <AppBarContext.Provider value="CARD_HEADER">
            <AppBar>
              <AppBarTitle>Sensors</AppBarTitle>
            </AppBar>
          </AppBarContext.Provider>
          <div className={styles.summarySection}>
            {sensors.map(sensor => (
              <li key={sensor.serial_number}>
                {sensor.name}
              </li>
            ))}
          </div>

          <AppBarContext.Provider value="CARD_HEADER">
            <AppBar>
              <AppBarTitle>Summary</AppBarTitle>
            </AppBar>
          </AppBarContext.Provider>
          <div className={styles.summarySection}>
            <DataGrid
              items={[
                {id: 1, label: 'Captures Per Schedule', value: capturesPerSchedule},
                {id: 2, label: 'Capture Schedules Each Week', value: numberOfSchedules},
                {
                  id: 3,
                  label: 'Amount of data collected each full week',
                  value: `${moment.duration(
                      captureDuration * numberOfSchedules * capturesPerSchedule * 1000
                  ).as('minutes')} minutes`,
                },
              ]}
            />
            {totalCaptures > CAPTURE_WARNING_THRESHOLD ? (
                <div className={styles.globalConfigurationWarning}>
                  <CaptureWarning/>
                </div>
            ) : null}
          </div>

          <AppBarContext.Provider value="CARD_HEADER">
            <AppBar>
              <AppBarTitle>Captures Each Week</AppBarTitle>
            </AppBar>
          </AppBarContext.Provider>

          <div className={styles.summarySection}>
            <ul className={styles.summaryMetrics}>
              <li>
                <strong>{totalCaptures}</strong> total {pluralize(totalCaptures, 'capture')}
              </li>
              <li>
                Ends at <strong>{scheduleEndDate.format('MM/DD/YYYY')}</strong>
              </li>
            </ul>
          </div>
          <svg width={svgWidth} height={svgHeight}>
            <g transform={`translate(${padding.left},${padding.top})`}>
              {/* Render a column for each day */}
              <g transform={`translate(${timeAxisWidth},0)`}>
                {DAYS_OF_WEEK.map((day, index) => (
                    <g key={day} transform={`translate(${weekColumnWidth * index}, 0)`}>
                      {/* Background */}
                      <rect
                          x={0}
                          y={0}
                          width={weekColumnWidth}
                          height={height}
                          fill={index % 2 === 0 ? colorVariables.gray100 : 'transparent'}
                      />

                      {/* Header at top of column */}
                      <text
                          transform={`translate(${weekColumnHeaderOffset}, 20)`}
                          textAnchor="middle"
                      >{day.slice(0, 3)}</text>

                      {/*
                                    Render a rectangle for each capture schedule that was created on the
                                    previous screen
                                    */}
                      {
                        state.schedules
                            .filter(schedule => schedule.daysOfWeek.includes(day))
                            .map(schedule => {
                              const duration = moment.duration(schedule.time);
                              if (!duration) {
                                return null;
                              }

                              const yPositionInSeconds = duration.asSeconds();
                              const yPositionInPx = yPositionInSeconds * pixelsPerSecond;

                              return (
                                  <rect
                                      key={schedule.time}
                                      x={0}
                                      y={weekColumnHeaderSchedulesOffset + yPositionInPx}
                                      width={weekColumnWidth}
                                      height={scheduleHeightInPx}
                                      fill={colorVariables.midnight}
                                  />
                              );
                            })
                      }

                      {/*
                                    Render schedules that happened the previous day but rolled over midnight
                                    until the current day
                                    */}
                      {
                        state.schedules
                            .filter(schedule => schedule.daysOfWeek.includes(DAYS_OF_WEEK[index - 1]))
                            .map(schedule => {
                              const duration = moment.duration(schedule.time);
                              if (!duration) {
                                return null;
                              }

                              const yPositionOfExistingScheduleInPx = duration.asSeconds() * pixelsPerSecond;
                              const rollOverScheduleHeightInPx = (
                                  scheduleHeightInPx - (columnHeight - yPositionOfExistingScheduleInPx)
                              );

                              if (rollOverScheduleHeightInPx > 0) {
                                return (
                                    <rect
                                        key={schedule.time}
                                        x={0}
                                        y={weekColumnHeaderSchedulesOffset}
                                        width={weekColumnWidth}
                                        height={rollOverScheduleHeightInPx}
                                        fill={colorVariables.midnight}
                                    />
                                );
                              } else {
                                return null;
                              }
                            })
                      }

                    </g>
                ))}
              </g>
            </g>

            {/* Render a scale to the left side of all the columns */}
            <g transform={`translate(0, ${padding.top + weekColumnHeaderSchedulesOffset})`}>
              {HOURS_IN_UTC_DAY.map(([time, label]) => {
                const position = moment.duration(time).asSeconds() * pixelsPerSecond;
                return (
                    <Fragment key={time}>
                      <text
                          transform={`translate(${padding.left + timeAxisWidth - 12},${position})`}
                          textAnchor="end"
                          alignmentBaseline="middle"
                          fontSize={12}
                      >
                        {label}
                      </text>
                      <line
                          x1={padding.left + timeAxisWidth}
                          y1={position}
                          x2={padding.left + width}
                          y2={position}
                          stroke={colorVariables.gray500}
                          shapeRendering="crispEdges"
                      />
                    </Fragment>
                );
              })}
            </g>
          </svg>
        </div>
        <AppBarContext.Provider value="BOTTOM_ACTIONS">
          <AppBar>
            <AppBarSection/>
            <AppBarSection>
              <ButtonGroup>
                <Button
                    variant="underline"
                    onClick={() => onDismiss()}
                >Cancel</Button>
                <BackButton page={state.page}/>
                <Button
                    variant="filled"
                    onClick={() => {
                      dispatch({
                        type: CaptureScheduleModalActionTypes.CAPTURE_SCHEDULE_MODAL_SET_PAGE,
                        page: PAGES_IN_ORDER[PAGES_IN_ORDER.indexOf(state.page) + 1],
                      });
                    }}
                >
                  Create {totalCaptureScheduleCount} Capture{' '}
                  Schedule{totalCaptureScheduleCount !== 1 ? 's' : ''}
                </Button>
              </ButtonGroup>
            </AppBarSection>
          </AppBar>
        </AppBarContext.Provider>
      </Fragment>
  );
};
export default SummaryPage;
